// QRScansDisplay.js - Updated to accept dateRange
import React from 'react';
import './QRScansDisplay.css';

const QRScanDisplay = ({ data, isLoading, error, insights = [], recommendations = [], dateRange = 'today' }) => {  
  if (error) {
    return (
      <div className="qr-error-container" style={{ color: '#ef4444' }}>
        <div className="qr-error-content">
          <div className="qr-error-icon">⚠️</div>
          <div className="qr-error-title">Error loading QR metrics</div>
          <div className="qr-error-message">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="qr-loading-container">
        <div className="qr-loading-content">
          <div className="qr-loading-spinner"></div>
          <div className="qr-loading-title">Loading QR metrics...</div>
        </div>
      </div>
    );
  }

  const { scans, conversionRate } = data || { scans: 0, conversionRate: 0 };

  // Helper function to get display text for date range
  const getDateRangeDisplayText = (range) => {
    const rangeMap = {
      'today': 'Today',
      'yesterday': 'Yesterday', 
      'week': 'Past Week',
      'month': 'Past Month',
      'year': 'Past Year',
      'all': 'All Time'
    };
    return rangeMap[range] || 'Selected Period';
  };

  // Determine status based on insights or conversion rate
  const getStatus = () => {
    if (insights.length > 0) {
      const positiveInsight = insights.find(i => i.type === 'positive');
      const warningInsight = insights.find(i => i.type === 'warning');
      
      if (positiveInsight) return { emoji: '🎯', text: 'Strong engagement', color: '#22c55e' };
      if (warningInsight) return { emoji: '💡', text: 'Room for improvement', color: '#f59e0b' };
    }
    
    if (conversionRate > 5) return { emoji: '🎯', text: 'Strong engagement', color: '#22c55e' };
    if (conversionRate > 2) return { emoji: '📈', text: 'Moderate engagement', color: '#667eea' };
    return { emoji: '💡', text: 'Room for improvement', color: '#f59e0b' };
  };

  const status = getStatus();

  return (
    <div className="qr-display-container">
      {/* Subtle gradient overlay */}
      <div className="qr-display-gradient"></div>
      
      {/* Header */}
      <div className="qr-display-header">
        <h3 className="qr-display-title">
          QR Code Scans
        </h3>
      </div>
      
      {/* Metrics Container */}
      <div className="qr-metrics-container">
        {/* QR Scans Metric */}
        <div className="qr-metric-card">
          <div className="qr-metric-value primary">
            {scans?.toLocaleString() || '0'}
          </div>
          <div className="qr-metric-label">
            Total Scans
          </div>
          
          {/* Decorative QR icon */}
          <div className="qr-metric-icon">
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="currentColor"
              style={{ color: '#667eea' }}
            >
              <path d="M3 11h8V3H3v8zm2-6h4v4H5V5zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM3 21h8v-8H3v8zm2-6h4v4H5v-4z"/>
              <path d="M18 13h-2v2h2v-2zM20 15h-2v2h2v-2zM16 15h-2v2h2v-2zM18 17h-2v2h2v-2z"/>
            </svg>
          </div>
        </div>
        
        {/* Conversion Rate Metric */}
        <div className="qr-metric-card">
          <div className="qr-metric-value success">
            {conversionRate?.toFixed(1) || '0.0'}%
          </div>
          <div className="qr-metric-label">
            Conversion Rate
          </div>
          
          {/* Decorative conversion icon */}
          <div className="qr-metric-icon">
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              style={{ color: '#22c55e' }}
            >
              <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7z"/>
              <path d="M17 17H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
            </svg>
          </div>
        </div>
      </div>
      
      {/* Bottom insight */}
      <div className="qr-status-card">
        <div className="qr-status-text" style={{ color: status.color }}>
          {status.emoji} {status.text}
        </div>
        
        {/* Show insights if available */}
        {insights.length > 0 && (
          <div className="qr-insight-text">
            {insights[0].text}
          </div>
        )}
        
        {/* Show recommendations if available */}
        {recommendations.length > 0 && (
          <div className="qr-recommendation">
            💡 {recommendations[0].text}
          </div>
        )}
      </div>
    </div>
  );
};

export default QRScanDisplay;